import { FC, useEffect, useState } from "react";
import { Box, Typography, Grid, Divider, IconButton } from "@mui/material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { MdBloodtype } from "react-icons/md";
import { QRCode } from "react-qrcode-logo";
import { isMobile, isTablet } from "react-device-detect";
import { Subscriber } from "../../Interfaces/subscriber";
import organDonor from "../../Assets/organ_donor.png";
import avatarPlaceholder from "../../Assets/user_placeholder.jpg";
import {
  useDocuments,
  useSubscriber,
  useSummaries,
} from "../../Hooks/useSuscribersService";
import useAuthUserData from "../../Hooks/useAuthUserData";
import SummaryAccordion from "./SummaryAccordion";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import { DEFAULT_SESSION_TIMEOUT, SCAN_METHODS } from "../../utils/constants";
import { getFamily } from "../../Services/userService";
import ProfileNavigationButton from "../ProfileNavigationButton/ProfileNavigationButton";
import SessionClassTimeoutModal from "../SessionClassTimeoutModal";

interface Props {
  idUser: string;
}

const SummarizeDetails: FC<Props> = ({ idUser }) => {
  const navState = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthUserData();
  const { subscriber } = useSubscriber(idUser);
  const { summaries, loading: summariesLoading } = useSummaries(idUser);

  const { avatar } = useDocuments(idUser, "avatar");
  const [familyMembers, setFamilyMembers] = useState<Subscriber[]>([]);
  const [seconds, setSeconds] = useState(30);
  const [startInterval, setStartInterval] = useState(false);
  const [startTimmeModal, setStartTimmeModal] = useState(true);

  /*Modal States*/
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setStartTimmeModal(true);
    setStartInterval(false);
    setSeconds(30);
  };

  // Add debug logging
  console.log("SummarizeDetails - Subscriber:", subscriber);
  console.log("SummarizeDetails - Summaries:", summaries);
  console.log("SummarizeDetails - Avatar:", avatar);

  useEffect(() => {
    if (subscriber) {
      getFamily(subscriber?.idParentMember ?? subscriber?.id).then((res) => {
        setFamilyMembers(res);
      });
    }
  }, [subscriber]);

  useEffect(() => {
    if (startTimmeModal && user?.activeSessionLength > 0) {
      setTimeout(() => {
        handleOpen();
        setSeconds(30);
        setStartInterval(true);
        setStartTimmeModal(false);
      }, (user?.activeSessionLength ?? DEFAULT_SESSION_TIMEOUT) * 60 * 1000);
    } else {
      console.log("🍉 ~ FrPersonalInfo ~ Timer was not created");
    }
  }, [startTimmeModal, subscriber]);

  useEffect(() => {
    subscriber?.accessCode == undefined
      ? localStorage.setItem("AccessCodeFirstResponder", "")
      : localStorage.setItem(
          "AccessCodeFirstResponder",
          subscriber?.accessCode
        );
  }, [subscriber]);

  useEffect(() => {
    if (startInterval) {
      if (seconds > 0) {
        setTimeout(() => setSeconds(seconds - 1), 1000);
      } else {
        handleClose();
        navigate("/");
      }
    }
  }, [startInterval, seconds]);

  function padTo2Digits(num: number) {
    return num.toString().padStart(2, "0");
  }

  function numberToMounth(num: number) {
    let arrayMonth = [
      "January ",
      "February ",
      "March ",
      "April ",
      "May ",
      "June ",
      "July ",
      "August ",
      "September ",
      "October ",
      "November ",
      "December ",
    ];
    return arrayMonth[num];
  }

  function formatDate(date: Date) {
    return [
      numberToMounth(date.getMonth()) + padTo2Digits(date.getDate()),
      date.getFullYear(),
    ].join(", ");
  }

  const scannedAccessCode = navState.state?.scannedAccessCode;

  const handleBack = () => {
    const scanMethod = navState.state?.scanMethod;
    if (scanMethod === SCAN_METHODS.biometric) {
      navigate("/SearchSubscriber");
    } else if (familyMembers.length > 1) {
      //Family group
      navigate(`/subscriberInfo?mmh_id=${scannedAccessCode || subscriber?.accessCode}`);
    } else {
      //Individual plan
      navigate("/SearchSubscriber");
    }
  };

  const handleGoToFullProfile = () => {
    navigate(`/subscriberInfo/details?mmh_id=${subscriber?.accessCode}`, {
      state: {
        scanMethod: navState.state?.scanMethod,
        registerId: navState.state?.registerId,
        scannedAccessCode: scannedAccessCode || subscriber?.accessCode,
      },
    });
  };

  const origin = window.location.origin;
  const baseUrl = origin.includes("localhost")
    ? "http://localhost:3001"
    : origin.includes("test")
    ? "https://test.firstresponder.mymedhistory.com"
    : origin.includes("dev")
    ? "https://dev.firstresponder.mymedhistory.com"
    : "https://firstresponder.mymedhistory.com";

  return (
    <Box>
      <SessionClassTimeoutModal
        open={open}
        handleClose={handleClose}
        seconds={seconds}
      />
      <IconButton
        aria-label="back"
        onClick={() => handleBack()}
        sx={{
          ml: 2,
          mb: -1,
        }}
      >
        <ArrowBackIcon
          sx={{
            color: "#fff",
          }}
        />
      </IconButton>
      <Box
        sx={{
          bgcolor: "white",
          color: "black",
          maxWidth: "960px",
          margin: "auto",
          pt: 2,
          position: "relative",
          "@media (max-width: 768px)": {
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Navigation Button - absolute positioned to right border */}
        <Box
          sx={{
            position: "absolute",
            right: 16,
            top: 24,
            zIndex: 1,
            "@media (max-width: 768px)": {
              position: "static",
              display: "flex",
              justifyContent: "center",
              order: -1,
              padding: "16px",
              width: "100%",
            },
          }}
        >
          <ProfileNavigationButton
            type="full"
            onClick={handleGoToFullProfile}
          />
        </Box>
        {/* Subscriber profile name and DOB section */}
        <Box
          sx={{
            bgcolor: "white",
            color: "black",
            maxWidth: "960px",
            margin: "auto",
          }}
        >
          {/* Subscriber profile name and DOB section */}
          <Box
            sx={{
              p: 2,
              textAlign: "center",
              position: "relative",
            }}
          >
            {/* Navigation Button - positioned on the left */}

            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              columnSpacing={1}
              item
            >
              <Grid item>
                <QRCode
                  value={`${baseUrl}/subscriberInfo?mmh_id=MMH${
                    subscriber?.accessCode || "none"
                  }&uuid=${subscriber?.id}`}
                  size={200}
                />
              </Grid>
              <Grid item>
                <Typography
                  fontWeight={600}
                  fontSize={15}
                  textAlign="start"
                  sx={{
                    color: "#555555",
                    "@media (max-width: 375px)": {
                      textAlign: "center",
                    },
                  }}
                >
                  Subscriber Profile
                </Typography>
                <Typography
                  fontWeight={800}
                  fontSize={28}
                  textAlign="start"
                  sx={{
                    "@media (max-width: 375px)": {
                      textAlign: "center",
                    },
                  }}
                  lineHeight={1.5}
                >
                  {subscriber?.firstName} {subscriber?.lastName}
                </Typography>
                <Typography
                  fontWeight={800}
                  textAlign="start"
                  fontSize={20}
                  sx={{
                    "@media (max-width: 375px)": {
                      textAlign: "center",
                    },
                  }}
                >
                  DOB:{" "}
                  {new Date(subscriber?.birthday || "").toLocaleDateString(
                    "en-US",
                    {
                      timeZone: "UTC",
                    }
                  )}
                </Typography>
              </Grid>
            </Grid>

            <Divider
              textAlign="center"
              sx={{
                color: "black",
                bgcolor: "black",
                margin: "0 auto",
                width: "55%",
                "@media (max-width: 600px)": {
                  width: "100%",
                },
              }}
            />

            {/* Organ donor, blood type */}
            <Grid
              container
              direction="row"
              alignItems="center"
              rowSpacing={3}
              columnSpacing={4}
              mt={2}
              mb={1}
            >
              {/* Organ donor */}
              <Grid
                xs={6}
                sm={6}
                direction="row"
                alignItems="center"
                columnSpacing={1}
                sx={{
                  display: "flex",
                  justifyContent: "end",
                  "@media (max-width: 600px)": {
                    justifyContent: "center",
                  },
                }}
              >
                <Grid item xs="auto">
                  <img
                    src={organDonor}
                    width={31}
                    height={60}
                    style={
                      subscriber?.organDonor
                        ? {}
                        : {
                            filter: "opacity(0.4)",
                          }
                    }
                  />
                </Grid>
                <Grid item xs={3} justifyContent="end">
                  <Typography
                    fontWeight={900}
                    fontSize={24}
                    sx={{
                      lineHeight: "20px",
                      "@media (max-width: 600px)": {
                        fontSize: "16px",
                      },
                      color: subscriber?.organDonor ? "#007036" : "#999999",
                    }}
                  >
                    {subscriber?.organDonor ? "ORGAN DONOR" : "NOT A DONOR"}
                  </Typography>
                </Grid>
              </Grid>
              {/* Blood type */}
              <Grid
                xs={6}
                sm={6}
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Grid item xs={3.5} sm={1.5}>
                  <MdBloodtype
                    style={{
                      fontSize: 60,
                      color: "#CE0000",
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    fontWeight={600}
                    fontSize={16}
                    sx={{
                      lineHeight: "14px",
                      width: "140px",
                    }}
                    align="justify"
                  >
                    BLOOD TYPE
                  </Typography>
                  <Typography
                    fontWeight={900}
                    fontSize={24}
                    align="justify"
                    sx={{
                      color: "#CE0000",
                    }}
                  >
                    {subscriber?.bloodType === "Unav"
                      ? "Unavailable"
                      : subscriber?.bloodType}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Divider
          sx={{
            color: "black",
            bgcolor: "black",
            width: "100%",
          }}
        />

        {/* MMH ID, Profile pic, Medication alerts, allergies section */}
        <Grid container direction="row" columnSpacing={4} rowSpacing={2} p={2}>
          {/* Profile pic */}
          <Grid item sm={12} md={5}>
            {/* Member id */}
            <Typography fontWeight={700} fontSize={25} lineHeight={1.5} pb={2}>
              <span
                style={{
                  color: "#000094",
                }}
              >
                MEMBER ID:
              </span>{" "}
              {subscriber?.accessCode}
            </Typography>

            <img
              src={
                Object.keys(avatar).length === 0 ? avatarPlaceholder : avatar
              }
              style={{
                objectFit: "cover",
                height: "28rem",
              }}
            />
          </Grid>

          {/* Summaries accordion*/}
          <Grid item sm={12} md={7}>
            <Typography
              fontWeight={700}
              fontSize={25}
              pb="16px"
              sx={{
                color: "#000094",
              }}
            >
              AI GENERATED SUMMARIES
            </Typography>
            <Box sx={{ width: "100%" }}>
              <SummaryAccordion
                title="Medication and Allergy Alerts"
                content={
                  summaries?.medicationalertsallergies?.summaryText || ""
                }
                ariaControls="summary2-content"
                id="medicationalertsallergies"
                sectionId="medication_alerts_allergies"
                //defaultExpanded
              />

              <Divider sx={{ margin: "8px 0", borderColor: "#e0e0e0" }} />
              <SummaryAccordion
                title="Key Medical Conditions"
                content={summaries?.medicalconditions?.summaryText || ""}
                ariaControls="summary3-content"
                id="medicalconditions"
                sectionId="medical_conditions"
              />

              <Divider sx={{ margin: "8px 0", borderColor: "#e0e0e0" }} />

              <SummaryAccordion
                title="Active Medications"
                content={summaries?.medications?.summaryText || ""}
                ariaControls="summary1-content"
                id="medications"
                sectionId="medications"
              />

              <Divider sx={{ margin: "8px 0", borderColor: "#e0e0e0" }} />

              <SummaryAccordion
                title="Surgical History "
                content={summaries?.surgeries?.summaryText || ""}
                ariaControls="summary4-content"
                id="surgeries"
                sectionId="surgeries"
              />

              <Divider sx={{ margin: "8px 0", borderColor: "#e0e0e0" }} />

              <SummaryAccordion
                title="Emergency Contacts"
                content={summaries?.emergencycontacts?.summaryText || ""}
                ariaControls="summary5-content"
                id="emergencycontacts"
                sectionId="emergency_contacts"
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default SummarizeDetails;
